import type { DataAttachmentType } from '@elara/domain/models/schemas/attachment-schemas';

import { ROUTES } from '@elara/client-core/constants';

import { useChatInputState, useNavigate } from '#states/hooks.js';
import { Button } from '#ui/button.js';

import { useCompactGuideActions } from './guide-header-actions.js';

export interface PlanTripCTAProps {
  /** The guide entity to seed the new trip with (e.g. `'hotel'` + the hotel id). */
  type: DataAttachmentType;
  id: string;
  name?: string;
  className?: string;
}

/**
 * Shown in a guide's action slot when the guide is opened outside a trip (e.g. from a Story) — the
 * usual Ask Elara / Shortlist / Itinerary actions all need a current trip. Starts a new trip seeded
 * with this hotel/place/destination: the entity is queued via `seedOnNextChat` (so it survives the
 * input reset that fires when the new trip's chat loads) and we navigate to `/trip/new`.
 */
export function PlanTripCTA({ type, id, name, className }: PlanTripCTAProps) {
  const chatInputState = useChatInputState();
  const { navigate } = useNavigate();
  // No icon says "plan a trip", so in the panel's floating header it stays a label, at the header's size.
  const compact = useCompactGuideActions();

  return (
    <Button
      variant="accent"
      size={compact ? 'sm' : undefined}
      className={className}
      onClick={() => {
        chatInputState.seedOnNextChat(type, id, name);
        navigate(ROUTES.TRIP_NEW);
      }}
    >
      Plan a trip
    </Button>
  );
}
